import {
  Building2,
  Landmark,
  Briefcase,
  IndianRupee,
  Wallet,
  BadgePercent,
  Clock3,
  ShieldCheck,
  CheckCircle2,
  Banknote,
  FileCheck,
  HandCoins,
  Users,
  FileText,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "LRD Program",
      path: "/lrd-program",
    },
  ]),
];

/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

export const seo = {
  title: "LRD Program (Lease Rental Discounting) | Money Mart",

  description:
    "Unlock the value of your leased commercial property with Money Mart's Lease Rental Discounting (LRD) Program. Get high-value funding against future rental income with competitive interest rates and quick approvals.",

  keywords:
    "LRD Program, Lease Rental Discounting, LRD Loan, Rental Income Loan, Commercial Property Loan, Lease Finance, Money Mart",

  canonical: "/lrd-program",

  structuredData: { schemas },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Rental Income Finance",

  title: "Unlock Funds Against Future Rental Income",

  description:
    "The LRD (Lease Rental Discounting) Program enables owners of leased commercial properties to raise substantial funds against future rental receivables. Money Mart helps you compare offers from leading Banks & NBFCs with competitive interest rates and flexible repayment options.",

  highlights: [
    "Funding Against Lease Rentals",
    "High-Value Loan Amounts",
    "Competitive Interest Rates",
    "Quick Processing & Disbursal",
  ],

  image: "/images/loans/lrd.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "LRD Program",

  title: "Finance Backed by Your Commercial Rental Income",

  description:
    "Lease Rental Discounting (LRD/LRD) is a financing solution where lenders provide loans against the future rental income generated from leased commercial properties. It enables property owners to unlock liquidity while continuing to earn rental income from long-term lease agreements.",

  items: [
    {
      icon: Building2,
      title: "Commercial Properties",
      description:
        "Finance against leased office spaces, retail outlets, commercial buildings, warehouses and other eligible commercial properties.",
    },
    {
      icon: HandCoins,
      title: "Rental Cash Flows",
      description:(
        <>
        LRD financing is assessed against <strong>current rental income</strong>, with an escrow account required. <strong>Up to 90%</strong> of the rental income can be used towards monthly loan repayment.
    </>)},
    {
      icon: Wallet,
      title: "Business Liquidity",
      description:
        "Access substantial funds without selling your income-generating commercial property.",
    },
    {
      icon: Landmark,
      title: "Trusted Lending Partners",
      description:
        "Compare financing solutions from leading Banks and NBFCs through Money Mart.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Choose the LRD Program?",

  description:
    "Lease Rental Discounting offers an efficient way to leverage stable rental income from commercial properties while retaining ownership of the asset.",

  items: [
    {
      icon: IndianRupee,
      title: "High Funding Eligibility",
      description:
        "Raise substantial funds based on future rental income and property value, subject to lender assessment.",
    },
    {
      icon: BadgePercent,
      title: "Competitive Interest Rates",
      description:
        "Benefit from attractive interest rates offered by leading Banks and NBFCs.",
    },
    {
      icon: Clock3,
      title: "Quick Loan Processing",
      description:
        "Receive faster approvals with expert assistance throughout the application process.",
    },
    {
      icon: ShieldCheck,
      title: "Continue Property Ownership",
      description:
        "Unlock capital while retaining ownership of your commercial property and lease income.",
    },
    {
      icon: Banknote,
      title: "Flexible End Use",
      description:
        "Use the funds for business expansion, debt restructuring, working capital, acquisitions or other eligible financial requirements.",
    },
    {
      icon: CheckCircle2,
      title: "Expert Guidance",
      description:
        "Our specialists help you compare lenders and identify the most suitable financing solution.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "LRD Solutions",

  title: "Lease Rental Discounting Solutions We Help Arrange",

  description:
    "Money Mart assists commercial property owners in securing Lease Rental Discounting facilities from multiple Banks & NBFCs based on their property profile and lease agreements.",

  items: [
    {
      icon: Building2,
      title: "Office Space LRD",
      description:
        "Funding against leased office buildings and corporate office spaces occupied by reputed tenants.",
    },
    {
      icon: Landmark,
      title: "Retail & Shopping Complex LRD",
      description:
        "Finance against leased retail shops, showrooms and shopping complexes with stable rental income.",
    },
    {
      icon: Briefcase,
      title: "Warehouse & Industrial LRD",
      description:
        "Raise funds against warehouses, logistics parks and industrial properties leased to established businesses.",
    },
    {
      icon: FileCheck,
      title: "Structured Lease Finance",
      description:
        "Customized financing solutions designed around long-term lease agreements and predictable rental cash flows.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                Eligibility                                 */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your LRD Program Eligibility",

  description:
    "Eligibility for the Lease Rental Discounting (LRD) Program depends on the leased commercial property, tenant profile, lease tenure, rental income, and the lender's internal credit policy.",

  requirements: [
    {
      icon: Building2,
      title: "Property Type",
      value:
        "Leased Commercial Property such as Offices, Retail Shops, Commercial Buildings, Warehouses, or Industrial Spaces",
    },
    {
      icon: FileCheck,
      title: "Lease Agreement",
      value: "Valid and enforceable lease agreement with an eligible tenant",
    },
    {
      icon: IndianRupee,
      title: "Rental Income",
      value: "Stable and verifiable rental income from the leased property",
    },
    {
      icon: Landmark,
      title: "Property Ownership",
      value: "Clear ownership and marketable title of the commercial property",
    },
  ],

  approvalFactors: [
    "Quality and creditworthiness of the tenant",
    "Remaining lease tenure",
    "Monthly rental income",
    "Commercial property valuation",
    "Location and marketability of the property",
    "Borrower's repayment profile and credit history",
  ],

  cta: {
    title: "Want to know your eligibility?",

    description:
      "Our experts will evaluate your leased commercial property and help you identify the most suitable Lease Rental Discounting solution.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for the LRD Program",

  description:
    "Documentation requirements may vary by lender. The following documents are commonly required while applying for Lease Rental Discounting.",

  groups: [
    {
      icon: Users,
      title: "Applicant Documents",

      documents: [
        "Passport Size Photograph",
        "PAN Card",
        "Aadhaar Card",
        "Address Proof",
      ],
    },
    {
      icon: Building2,
      title: "Property Documents",

      documents: [
        "Title Deed",
        "Sale Deed / Conveyance Deed",
        "Property Tax Receipts",
        "Approved Building Plan (if applicable)",
        "Occupancy / Completion Certificate (if applicable)",
      ],
    },
    {
      icon: FileCheck,
      title: "Lease Documents",

      documents: [
        "Registered Lease Agreement",
        "Rental Receipts",
        "Tenant Details",
        "Security Deposit Details (if applicable)",
      ],
    },
    {
      icon: Briefcase,
      title: "Financial Documents",

      documents: [
        "Last 2–3 Years Income Tax Returns",
        "Bank Statements",
        "Financial Statements (for business applicants)",
      ],
    },
    {
      icon: Landmark,
      title: "Banking Documents",

      documents: [
        "Last 6 Months Bank Statements",
        "Existing Loan Details (if applicable)",
      ],
    },
    {
      icon: FileText,
      title: "Additional Documents",

      documents: [
        "Rental Income Proof",
        "Property Valuation Report (if required)",
        "Any additional documents requested by the lender",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Get LRD Financing in 6 Simple Steps",

  description:
    "From evaluating your leased commercial property and rental income to lender approval and loan disbursal, Money Mart assists you throughout the entire Lease Rental Discounting process.",
};

/* -------------------------------------------------------------------------- */
/*                              Why Money Mart                                */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Choose Money Mart for the LRD Program?",

  description:
    "We help commercial property owners compare Lease Rental Discounting solutions from leading Banks & NBFCs, ensuring competitive terms, transparent guidance, and a hassle-free financing experience.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "LRD Program FAQs",

  description:
    "Find answers to the most commonly asked questions about the Lease Rental Discounting (LRD) Program.",

  items: [
    {
      question: "What is the LRD Program?",

      answer:
        "The LRD (Lease Rental Discounting) Program is a financing solution where loans are sanctioned against the future rental income generated from leased commercial properties. The funding is based on factors such as rental income, lease tenure, tenant profile, and property valuation.",
    },
    {
      question: "Which properties are eligible for LRD financing?",

      answer:
        "Commercial properties such as office spaces, retail shops, shopping complexes, commercial buildings, warehouses, and industrial properties with valid lease agreements are generally eligible, subject to lender policies.",
    },
    {
      question: "Can I continue earning rent after taking an LRD loan?",

      answer:
        "Yes. The rental income continues as per the agreed financing structure. The exact payment mechanism depends on the lender and loan agreement.",
    },
    {
      question: "How is the loan amount determined?",

      answer:
        "The loan amount depends on multiple factors including rental income, lease tenure, tenant quality, commercial property valuation, and the lender's credit assessment.",
    },
    {
      question: "What can I use the funds for?",

      answer:
        "LRD funds are commonly used for business expansion, working capital, refinancing existing debt, purchasing business assets, investments, or other eligible business requirements.",
    },
    {
      question: "How long does the approval process take?",

      answer:
        "Approval timelines vary depending on documentation, property verification, and lender processes. Applications with complete documentation generally receive faster processing.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Unlock the Value of Your Rental Income",

  title: "Compare Lease Rental Discounting Offers from Leading Banks & NBFCs",

  description:
    "Let Money Mart help you secure the right Lease Rental Discounting solution with competitive interest rates, higher funding eligibility, and expert guidance throughout the financing process.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for LRD Program",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Lead Form                                  */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free LRD Consultation",

  title: "Speak to an LRD Finance Expert",

  description:
    "Our specialists will evaluate your leased commercial property and help you compare Lease Rental Discounting solutions from multiple Banks & NBFCs.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our LRD financing expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Applicant Type",
      income: "Monthly Rental Income",
      loanAmount: "Required Loan Amount",
    },

    incomeField: "monthlyIncome",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-1L",
        label: "Below ₹1 Lakh",
      },
      {
        value: "1L-3L",
        label: "₹1 Lakh – ₹3 Lakhs",
      },
      {
        value: "3L-5L",
        label: "₹3 Lakhs – ₹5 Lakhs",
      },
      {
        value: "5L-10L",
        label: "₹5 Lakhs – ₹10 Lakhs",
      },
      {
        value: "10L-plus",
        label: "Above ₹10 Lakhs",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your Lease Rental Discounting enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const lrdProgram = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default lrdProgram;
